import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Badge, Button, ButtonGroup, Card, CardBody, Col, Container, Row, Spinner, Table } from "reactstrap";
import { Link, useSearchParams } from "react-router-dom";
import ResearchDisclaimer from "../../Components/Common/ResearchDisclaimer";
import { getPublicPicks } from "../../services/userPredictionService";
import FilterBar from "./FilterBar";
import DateFilter, { describeDateFilter } from "./DateFilter";
import Pagination from "./Pagination";

/**
 * Every public, counted single-asset call on the site, in one table:
 * members' shared research and each mode's settled picks, one row per stock.
 *
 * Nothing private is listed, and nothing the statistics leave out -- a forecast
 * made outside market hours against a close the model had already seen past,
 * or a mode run created after its window began. So the table is the record the
 * research hub's figures are counted from, and it can be exported whole.
 *
 * Filters live in the URL, as on /research, so a combination is a link.
 */
const API = process.env.REACT_APP_API_URL || "http://localhost:5000";
const PAGE_SIZE = 50;

const FACETS = [
  { key: "symbols", label: "Symbol", icon: "ri-coin-line" },
  { key: "horizons", label: "Horizon", icon: "ri-time-line" },
  { key: "models", label: "Model", icon: "ri-robot-line" },
  { key: "prompts", label: "Prompt", icon: "ri-file-text-line" },
];
const DATE_KEYS = ["createdFrom", "createdTo", "targetFrom", "targetTo"];

const fmtTime = (v) => (v ? new Date(v).toLocaleString([], { dateStyle: "short", timeStyle: "short" }) : "—");
const fmtPrice = (v) => (v === null || v === undefined ? "—" : `$${Number(v) >= 100 ? Number(v).toFixed(2) : Number(v).toPrecision(4)}`);
const fmtPct = (v) => (v === null || v === undefined ? "" : `${v >= 0 ? "+" : ""}${Number(v).toFixed(2)}%`);

const Direction = ({ dir, pct }) => {
  if (!dir) return <span className="text-muted">—</span>;
  if (dir === "none") return <span className="text-muted">No call</span>;
  if (dir === "flat") return <span className="text-muted">Flat {fmtPct(pct)}</span>;
  return (
    <span className="text-nowrap">
      <i className={`${dir === "up" ? "ri-arrow-up-line" : "ri-arrow-down-line"} align-bottom me-1`} />
      {dir === "up" ? "Up" : "Down"}{pct !== null && pct !== undefined ? ` ${fmtPct(pct)}` : ""}
    </span>
  );
};

const Verdict = ({ row }) => {
  if (!row.settled) return <Badge color="light" className="text-body">Open</Badge>;
  if (row.right === null) return <Badge color="light" className="text-body">No call</Badge>;
  return row.right ? <Badge color="success">Right</Badge> : <Badge color="danger">Wrong</Badge>;
};

const PublicPicks = () => {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ data: [], total: 0, page: 1, totalPages: 1, facets: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const filters = useMemo(() => Object.fromEntries(
    FACETS.map(({ key }) => [key, (params.get(key) || "").split(",").filter(Boolean)])
  ), [params]);
  const dates = useMemo(() => {
    const d = {};
    DATE_KEYS.forEach((k) => { if (params.get(k)) d[k] = params.get(k); });
    if (params.get("datePreset")) d.preset = params.get("datePreset");
    return Object.keys(d).length ? d : null;
  }, [params]);
  const status = params.get("status") || "";
  const page = Math.max(parseInt(params.get("page"), 10) || 1, 1);

  useEffect(() => { document.title = "Public picks | ForecastNest"; }, []);

  const update = useCallback((changes) => {
    const next = new URLSearchParams(params);
    Object.entries(changes).forEach(([k, v]) => {
      if (v == null || v === "" || (Array.isArray(v) && !v.length)) next.delete(k);
      else next.set(k, Array.isArray(v) ? v.join(",") : v);
    });
    setParams(next, { replace: true });
  }, [params, setParams]);

  // The query every view of this table shares: the page, and the export.
  const query = useMemo(() => {
    const q = {};
    FACETS.forEach(({ key }) => { if (filters[key].length) q[key] = filters[key].join(","); });
    DATE_KEYS.forEach((k) => { if (dates && dates[k]) q[k] = dates[k]; });
    if (status) q.status = status;
    return q;
  }, [filters, dates, status]);

  useEffect(() => {
    let live = true;
    setLoading(true);
    getPublicPicks({ ...query, page, limit: PAGE_SIZE })
      .then((res) => { if (live) { setData(res); setError(null); } })
      .catch((e) => { if (live) setError((typeof e === "string" && e) || e?.message || "Could not load the public picks."); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [query, page]);

  const exportUrl = `${API.replace(/\/$/, "")}/api/public-picks?${new URLSearchParams({ ...query, format: "csv" })}`;
  const dateSummary = describeDateFilter(dates);

  return (
    <div className="page-content" style={{ paddingTop: 118 }}>
      <Container fluid>
        <Row className="justify-content-center">
          <Col xxl={11}>
            <h4 className="mb-1">Public picks</h4>
            <p className="text-muted mb-2">
              Every public call on ForecastNest, one row per asset: members' shared research and each
              mode's settled picks. Forecasts left out of the statistics — made outside market hours
              against a close the model had already seen past, or created after their window began —
              are not listed, and nothing private is. <Link to="/how-scoring-works">How scoring works</Link>.
            </p>
            <ResearchDisclaimer className="mb-3" />

            <Card>
              <CardBody>
                <FilterBar
                  facetList={FACETS}
                  facets={data.facets}
                  selected={filters}
                  onChange={(next) => update({ ...next, page: null })}
                  loading={loading}
                  extraControls={<>
                    <ButtonGroup size="sm" className="me-2">
                      {[["", "All"], ["settled", "Settled"], ["open", "Open"]].map(([v, label]) => (
                        <Button key={v || "all"} color={status === v ? "primary" : "light"} disabled={loading}
                                onClick={() => update({ status: v, page: null })}>{label}</Button>
                      ))}
                    </ButtonGroup>
                    <DateFilter value={dates} loading={loading} onChange={(next) => update({
                      ...DATE_KEYS.reduce((a, k) => ({ ...a, [k]: (next || {})[k] || null }), {}),
                      datePreset: (next || {}).preset || null, page: null,
                    })} />
                  </>}
                  extraChips={dateSummary && (
                    <Badge color="primary" className="d-inline-flex align-items-center">
                      <span className="opacity-75 me-1">Date:</span>{dateSummary}
                      <button type="button" className="btn-close btn-close-white ms-2" style={{ fontSize: "0.5rem" }}
                              aria-label="Remove date filter"
                              onClick={() => update({ ...DATE_KEYS.reduce((a, k) => ({ ...a, [k]: null }), {}), datePreset: null, page: null })} />
                    </Badge>
                  )}
                />

                <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                  <span className="text-muted fs-13">
                    {loading ? "Loading…" : `${data.total.toLocaleString()} pick${data.total === 1 ? "" : "s"}`}
                  </span>
                  <a className={`btn btn-soft-primary btn-sm${!data.total ? " disabled" : ""}`} href={exportUrl} download>
                    <i className="ri-download-2-line align-bottom me-1" />Export CSV
                  </a>
                </div>

                {error ? (
                  <p className="text-muted text-center py-4">{error}</p>
                ) : loading && !data.data.length ? (
                  <div className="text-center py-4"><Spinner /></div>
                ) : !data.data.length ? (
                  <p className="text-muted text-center py-4">No public pick matches this filter.</p>
                ) : (
                  <div className="table-responsive">
                    <Table className="align-middle table-nowrap mb-0 fs-13">
                      <thead className="table-light">
                        <tr>
                          <th>Symbol</th><th>Model</th><th>Prompt</th><th>Predicted</th><th>Ends</th>
                          <th className="text-end">Start price</th><th className="text-end">Model's price</th>
                          <th>Called</th><th>Actual</th><th title="SPY over the same window">SPY</th><th>Result</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.data.map((r) => (
                          <tr key={`${r.link}:${r.symbol}`}>
                            <td className="fw-medium"><Link to={r.link}>{r.symbol}</Link></td>
                            <td>{r.model}</td>
                            <td className="text-wrap" style={{ minWidth: 140 }}>{r.prompt}<span className="text-muted"> · {r.horizon}</span></td>
                            <td>{fmtTime(r.predictedAt)}</td>
                            <td>{fmtTime(r.endsAt)}</td>
                            <td className="text-end">{fmtPrice(r.startPrice)}</td>
                            <td className="text-end">{fmtPrice(r.reportedPrice)}</td>
                            <td>
                              <Direction dir={r.calledDirection} pct={r.calledMovePct} />
                              {r.rangeLow != null && <div className="text-muted fs-11">{fmtPrice(r.rangeLow)} – {fmtPrice(r.rangeHigh)}</div>}
                            </td>
                            <td><Direction dir={r.actualDirection} pct={r.actualMovePct} /></td>
                            <td className="text-muted">
                              {r.spyMovePct == null ? "—" : <Direction dir={r.spyMovePct > 0 ? "up" : r.spyMovePct < 0 ? "down" : "flat"} pct={r.spyMovePct} />}
                            </td>
                            <td><Verdict row={r} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </CardBody>
            </Card>

            {!loading && (
              <Pagination page={data.page} totalPages={data.totalPages} total={data.total} pageSize={data.pageSize || PAGE_SIZE}
                          onPage={(n) => { update({ page: n <= 1 ? null : n }); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                          noun="pick">
                Member calls read a range against the price the model found; a mode's pick is the stock it
                chose, long or short. Actual is the move from the start price over the window, and SPY is
                the benchmark over that same window.
              </Pagination>
            )}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default PublicPicks;
